import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ApiService, showToast } from './api.service';

@Component({
  selector: 'app-admin-purchase-order-suppliers',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="min-h-full overflow-y-auto admin-premium-bg">
      <div class="max-w-[1400px] mx-auto p-5 space-y-5">
        <header class="flex items-start justify-between gap-4">
          <div>
            <a routerLink="/admin/financials/purchase-orders" class="inline-flex items-center gap-1.5 text-slate-500 hover:text-[#1A6FA6] font-semibold text-xs mb-1">
              <i class="ti ti-arrow-left"></i>Purchase Orders
            </a>
            <h1 class="text-xl font-semibold text-slate-950 tracking-tight">PO Suppliers</h1>
            <p class="text-slate-500 text-xs mt-1">Manage supplier details used on purchase orders and supplier emails.</p>
          </div>
          <button type="button" (click)="openSupplierModal()" class="h-9 px-3 rounded-lg bg-[#0C3A5A] text-white text-xs font-bold hover:bg-[#082d46] flex items-center gap-2">
            <i class="ti ti-plus text-[16px]"></i>Add Supplier
          </button>
        </header>

        <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden">
          <div class="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <div class="relative flex-1 max-w-md">
              <i class="ti ti-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[16px]"></i>
              <input [(ngModel)]="search" (input)="loadSuppliers()" type="text" placeholder="Search supplier, email or phone"
                class="w-full h-9 pl-9 pr-3 rounded-lg text-xs border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:border-[#1A6FA6]">
            </div>
          </div>

          <div class="overflow-x-auto min-h-[420px]">
            <table class="w-full text-left text-sm">
              <thead>
                <tr class="bg-slate-50/80 border-b border-slate-100">
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Supplier</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Contact</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">GST / Account</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Address</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100">
                @for (supplier of suppliers(); track supplier.id) {
                  <tr class="hover:bg-slate-50">
                    <td class="px-4 py-3">
                      <p class="font-semibold text-slate-900 text-sm">{{ supplier.name }}</p>
                      <p class="text-[10px] text-slate-400">ID {{ supplier.id }}</p>
                    </td>
                    <td class="px-4 py-3 text-xs text-slate-500">
                      <p class="font-semibold text-slate-700">{{ supplier.contact_person || '-' }}</p>
                      <p>{{ supplier.email || '-' }}</p>
                      <p>{{ supplier.phone || '-' }}</p>
                    </td>
                    <td class="px-4 py-3 text-xs text-slate-500">
                      <p>GST: {{ supplier.gst_number || '-' }}</p>
                      <p>Acct: {{ supplier.account_number || '-' }}</p>
                    </td>
                    <td class="px-4 py-3 text-xs text-slate-500 max-w-sm whitespace-pre-line">{{ supplier.address || '-' }}</td>
                    <td class="px-4 py-3">
                      <div class="flex items-center justify-end gap-2">
                        <button type="button" (click)="openSupplierModal(supplier)" class="h-8 px-3 rounded-lg border border-sky-200 bg-sky-50 text-sky-700 text-xs font-semibold hover:bg-sky-100">Edit</button>
                        <button type="button" (click)="deleteSupplier(supplier)" class="h-8 px-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-xs font-semibold hover:bg-rose-100">Delete</button>
                      </div>
                    </td>
                  </tr>
                }
                @if (!isLoading() && suppliers().length === 0) {
                  <tr>
                    <td colspan="5" class="py-16 text-center text-slate-400">
                      <i class="ti ti-building-store text-4xl mb-3 opacity-25"></i>
                      <p class="font-semibold text-sm">No suppliers found.</p>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>

    @if (showSupplierModal()) {
      <div class="fixed inset-0 z-[100] bg-slate-950/40 backdrop-blur-sm flex items-center justify-center p-4">
        <div class="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-3xl overflow-hidden">
          <div class="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <p class="text-sm font-bold text-slate-900">{{ supplierForm.id ? 'Edit Supplier' : 'Add Supplier' }}</p>
              <p class="text-[11px] text-slate-500">These details appear on purchase orders and supplier emails.</p>
            </div>
            <button type="button" (click)="showSupplierModal.set(false)" class="w-8 h-8 rounded-lg bg-slate-50 text-slate-500 hover:bg-slate-100"><i class="ti ti-x"></i></button>
          </div>
          <div class="p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
            <label class="text-xs font-semibold text-slate-600">Supplier name <span class="text-rose-500">*</span>
              <input [(ngModel)]="supplierForm.name" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
            </label>
            <label class="text-xs font-semibold text-slate-600">Email
              <input [(ngModel)]="supplierForm.email" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
            </label>
            <label class="text-xs font-semibold text-slate-600">Phone
              <input [(ngModel)]="supplierForm.phone" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
            </label>
            <label class="text-xs font-semibold text-slate-600">Contact person
              <input [(ngModel)]="supplierForm.contact_person" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
            </label>
            <label class="text-xs font-semibold text-slate-600">GST number
              <input [(ngModel)]="supplierForm.gst_number" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
            </label>
            <label class="text-xs font-semibold text-slate-600">Account number
              <input [(ngModel)]="supplierForm.account_number" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
            </label>
            <label class="md:col-span-3 text-xs font-semibold text-slate-600">Address
              <textarea [(ngModel)]="supplierForm.address" rows="3" class="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-xs"></textarea>
            </label>
            <label class="md:col-span-3 text-xs font-semibold text-slate-600">Notes
              <textarea [(ngModel)]="supplierForm.notes" rows="2" class="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-xs"></textarea>
            </label>
          </div>
          <div class="px-5 py-4 border-t border-slate-100 flex justify-end gap-2">
            <button type="button" (click)="showSupplierModal.set(false)" class="h-9 px-4 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-semibold hover:bg-slate-50">Cancel</button>
            <button type="button" (click)="saveSupplier()" [disabled]="isSaving()" class="h-9 px-4 rounded-lg bg-[#0C3A5A] text-white text-xs font-bold hover:bg-[#082d46] disabled:opacity-50">Save Supplier</button>
          </div>
        </div>
      </div>
    }
  `
})
export class AdminPurchaseOrderSuppliersComponent implements OnInit {
  private api = inject(ApiService);
  suppliers = signal<any[]>([]);
  isLoading = signal(false);
  isSaving = signal(false);
  showSupplierModal = signal(false);
  search = '';
  supplierForm: any = this.emptySupplierForm();

  ngOnInit() {
    this.loadSuppliers();
  }

  emptySupplierForm() {
    return { id: null, name: '', email: '', phone: '', contact_person: '', address: '', gst_number: '', account_number: '', notes: '', is_active: 1 };
  }

  loadSuppliers() {
    this.isLoading.set(true);
    this.api.getSuppliers(this.search).subscribe({
      next: (res: any) => {
        this.suppliers.set(res.data?.suppliers || []);
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false)
    });
  }

  openSupplierModal(supplier: any = null) {
    this.supplierForm = supplier ? { ...this.emptySupplierForm(), ...supplier, is_active: supplier.is_active ?? 1 } : this.emptySupplierForm();
    this.showSupplierModal.set(true);
  }

  saveSupplier() {
    if (!this.supplierForm.name?.trim()) return showToast('error', 'Supplier name is required.');
    this.isSaving.set(true);
    this.api.saveSupplier(this.supplierForm).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.showSupplierModal.set(false);
        showToast('success', 'Supplier saved.');
        this.loadSuppliers();
      },
      error: (err) => {
        this.isSaving.set(false);
        showToast('error', err.error?.message || 'Could not save supplier.');
      }
    });
  }

  deleteSupplier(supplier: any) {
    if (!confirm(`Delete supplier ${supplier.name}? Existing purchase orders will keep their history.`)) return;
    this.api.deleteSupplier(supplier.id).subscribe({
      next: () => {
        showToast('success', 'Supplier deleted.');
        this.loadSuppliers();
      },
      error: (err) => showToast('error', err.error?.message || 'Could not delete supplier.')
    });
  }
}
